import React, { useEffect, useMemo, useState } from "react";
import Dialog from "../../../Components/common/Dialog";
import HealthCard from "./HealthCard";
import { fetchUpcomingMaintenanceDetails } from "../api";
import { BAR_COLORS, formatNumber } from "../utils";

const WINDOWS = [
  { key: 7, labelKey: "next7Days" },
  { key: 30, labelKey: "next30Days" },
  { key: 60, labelKey: "next60Days" },
];

/**
 * fmd/19 shape:
 * { next7Days: {}, next30Days: { "Tyre Rotation": 12 }, next60Days: { ... } }
 */
const normalizeServices = (data, windowKey) => {
  if (!data) return { services: [], vehiclesAffected: 0, estimatedCost: 0 };

  const windowData =
    data[`next${windowKey}Days`] || data[`next${windowKey}`] || {};

  const services = Object.entries(windowData)
    .filter(
      ([, value]) => typeof value === "number" || typeof value === "string",
    )
    .map(([name, count]) => ({
      name,
      count: Number(count) || 0,
    }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const vehiclesAffected = services.reduce((sum, item) => sum + item.count, 0);
  const estimatedCost = Number(data?.estimatedCost ?? 0) || 0;

  return { services, vehiclesAffected, estimatedCost };
};

const UpcomingMaintenance = ({ translate, data, loading, onScheduleAll }) => {
  const [windowKey, setWindowKey] = useState(7);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsService, setDetailsService] = useState(null);
  const [plateNumbers, setPlateNumbers] = useState([]);
  const [plateSearch, setPlateSearch] = useState("");

  const { services, vehiclesAffected, estimatedCost } = useMemo(
    () => normalizeServices(data, windowKey),
    [data, windowKey],
  );
  const max = Math.max(...services.map((s) => s.count), 1);
  const rangeParam = `next${windowKey}Days`;

  useEffect(() => {
    setDetailsOpen(false);
    setDetailsService(null);
    setPlateNumbers([]);
    setPlateSearch("");
  }, [windowKey]);

  const filteredPlates = useMemo(() => {
    const q = plateSearch.trim().toLowerCase();
    if (!q) return plateNumbers;
    return plateNumbers.filter((plate) =>
      String(plate).toLowerCase().includes(q),
    );
  }, [plateNumbers, plateSearch]);

  const openServiceDetails = async (service) => {
    if (!service?.name) return;
    setDetailsService(service);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setPlateNumbers([]);
    setPlateSearch("");
    try {
      const res = await fetchUpcomingMaintenanceDetails({
        range: rangeParam,
        itemCode: service.name,
      });
      const plates = Array.isArray(res?.plateNumbers)
        ? res.plateNumbers
        : Array.isArray(res?.data?.plateNumbers)
          ? res.data.plateNumbers
          : [];
      setPlateNumbers(plates);
    } catch (e) {
      setPlateNumbers([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsService(null);
    setPlateNumbers([]);
    setPlateSearch("");
  };

  return (
    <>
      <HealthCard
        className="fh-span-4"
        title={translate("upcomingMaintenance")}
        subtitle={translate("upcomingMaintenanceSub")}
        loading={loading}
      >
        <div className="fh-seg">
          {WINDOWS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={windowKey === item.key ? "on" : ""}
              onClick={() => setWindowKey(item.key)}
            >
              {translate(item.labelKey)}
            </button>
          ))}
        </div>

        <div className="fh-maint-list" style={{ paddingRight: 4 }}>
          {services.length ? (
            services.map((service, index) => (
              <div
                className="fh-bar-row fh-bar-row-clickable"
                key={`${service.name}-${index}`}
                role="button"
                tabIndex={0}
                onClick={() => openServiceDetails(service)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openServiceDetails(service);
                  }
                }}
              >
                <div className="fh-bar-top">
                  <span>{service.name}</span>
                  <b>{service.count}</b>
                </div>
                <div className="fh-track">
                  <div
                    className="fh-fill"
                    style={{
                      width: `${(service.count / max) * 100}%`,
                      background: BAR_COLORS[index % BAR_COLORS.length],
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="fh-empty">{translate("nothingDueInWindow")}</div>
          )}
        </div>

        {/*  <div className="fh-metaline">
          <span>{translate("vehiclesAffected")}</span>
          <b>{formatNumber(vehiclesAffected)}</b>
        </div>
        <div className="fh-metaline">
          <span>{translate("estimatedCost")}</span>
          <b>
            {estimatedCost
              ? `${formatNumber(estimatedCost)} ${translate("aed")}`
              : "—"}
          </b>
        </div>*/}
      </HealthCard>

      {detailsOpen ? (
        <Dialog
          onClose={closeDetails}
          title={
            detailsService
              ? `${detailsService.name} · ${translate(
                  WINDOWS.find((w) => w.key === windowKey)?.labelKey ||
                    "upcomingMaintenance",
                )}`
              : translate("upcomingMaintenance")
          }
          maxWidth="md"
          fullWidth
          isVisableBtn
          cancelText={translate("sharedCancel")}
        >
          <div style={{ padding: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                value={plateSearch}
                onChange={(e) => setPlateSearch(e.target.value)}
                placeholder={translate("searchPlate")}
                style={{
                  flex: 1,
                  minWidth: 160,
                  padding: "7px 10px",
                  border: "1px solid #e3e7eb",
                  borderRadius: 4,
                  fontSize: 12,
                }}
              />
              <b style={{ fontSize: 12, color: "#5c6a76" }}>
                {formatNumber(filteredPlates.length)} /{" "}
                {formatNumber(plateNumbers.length)}
              </b>
            </div>

            {detailsLoading ? (
              <div className="fh-empty">Loading…</div>
            ) : filteredPlates.length ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                {filteredPlates.map((plate, index) => (
                  <span key={`${plate}-${index}`} className="fh-plate">
                    {plate}
                  </span>
                ))}
              </div>
            ) : (
              <div className="fh-empty">
                {plateSearch.trim()
                  ? translate("noPlatesMatch")
                  : translate("noRecordFound")}
              </div>
            )}

            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button
                type="button"
                className="fh-btn ghost"
                onClick={closeDetails}
              >
                {translate("close") || translate("sharedCancel")}
              </button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
};

export default UpcomingMaintenance;
